// Pure, Redux-free formatter turning ONE canonical game-log event into a renderable line
// descriptor. This is the SINGLE source of log-line text — the live ActionLog sidebar and the
// upcoming replay viewer both format the same raw events (live push / GET /handhistory) through
// this function, so it must never read Redux: name/card resolution is injected via `ctx`.
//
// Event shape: { id, seq, ts, type, actorSeat, visibility, payload } (see EventLogService).
// `ctx` = { resolveSeatName(seat)->string, resolvePupName(code)->string, viewerSeat:number|null }.
//
// Returns a LogLine, an array of LogLine (HAND_START only), or null (suppress — e.g. an event
// that carries no viewer-visible line). LogLine:
//   { key, seq, kind:'divider'|'line', category:'action'|'pup'|'structural', text, segments[] }
// Segment ∈ { type:'text', text } | { type:'card', token, rank, suit, glyph, color, text }
//                                  | { type:'pup', code, name, text }.
// INVARIANT (unit-tested): line.text === line.segments.map(s => s.text).join('').

import { formatAmount } from '../utilities/chips';

// Suit glyphs + colors, matching the maps duplicated across the card renderers
// (CommunityCards.js / HoleCards.js / CardMods.js). Exported so the replay viewer can reuse them.
export const SUIT_GLYPH = { h: '♥', d: '♦', c: '♣', s: '♠' };
export const SUIT_COLOR = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

const rankDisplay = (rank) => (rank === 'T' ? '10' : rank);

// One playing-card token ("Ah") -> a colored `card` segment. Returns null for a bad token.
export const cardSegment = (token) => {
  if (typeof token !== 'string' || token.length < 2) return null;
  const suit = token.slice(-1);
  const rank = token.slice(0, -1);
  const glyph = SUIT_GLYPH[suit] || suit;
  return {
    type: 'card',
    token,
    rank,
    suit,
    glyph,
    color: SUIT_COLOR[suit] || 'inherit',
    text: rankDisplay(rank) + glyph,
  };
};

// "Ah Kd" (string) or ["Ah","Kd"] (array) -> [cardSegment, ...] (bad tokens dropped).
export const cardSegments = (cards) => {
  if (!cards) return [];
  const list = Array.isArray(cards) ? cards : String(cards).trim().split(/\s+/);
  return list.map(cardSegment).filter(Boolean);
};

const textSeg = (t) => ({ type: 'text', text: t });
const pupSeg = (code, name) => ({ type: 'pup', code, name, text: name });

// A prefix string followed by space-separated card segments: "Your cards: " + A♥ K♦.
const withCards = (prefix, cards) => {
  const segs = [textSeg(prefix)];
  cardSegments(cards).forEach((c, i) => {
    if (i > 0) segs.push(textSeg(' '));
    segs.push(c);
  });
  return segs;
};

const makeLine = (event, category, segments, kind = 'line', idx = null) => ({
  key: idx == null ? String(event.seq) : `${event.seq}:${idx}`,
  seq: event.seq,
  kind,
  category,
  text: segments.map((s) => s.text).join(''),
  segments,
});

export const formatLogEvent = (event, ctx) => {
  if (!event || typeof event.type !== 'string' || !ctx) return null;
  const p = event.payload || {};
  const name = (seat) => ctx.resolveSeatName(seat);
  const pupName = (code) => ctx.resolvePupName(code);
  const viewer = ctx.viewerSeat;

  switch (event.type) {
    case 'HAND_START': {
      const lines = [makeLine(event, 'structural', [textSeg('New hand')], 'divider', 0)];
      if (p.button != null) {
        lines.push(makeLine(event, 'structural', [textSeg(`${name(p.button)} is on the button`)], 'line', 1));
      }
      if (p.smallBlindSeat != null) {
        lines.push(makeLine(event, 'structural',
          [textSeg(`${name(p.smallBlindSeat)} posts small blind ${formatAmount(p.smallBlind)}`)], 'line', 2));
      }
      if (p.bigBlindSeat != null) {
        lines.push(makeLine(event, 'structural',
          [textSeg(`${name(p.bigBlindSeat)} posts big blind ${formatAmount(p.bigBlind)}`)], 'line', 3));
      }
      return lines;
    }

    case 'HOLE_CARDS_DEALT': {
      // Private to its owner; the server only sends it to that seat, and we double-check here so a
      // shared formatter can never surface another seat's hole cards in a replay.
      if (viewer == null || event.actorSeat !== viewer) return null;
      return makeLine(event, 'structural', withCards('Your cards: ', p.cards));
    }

    case 'PLAYER_ACTION': {
      const who = name(event.actorSeat);
      let verb;
      switch (p.action) {
        case 'fold': verb = 'folds'; break;
        case 'check': verb = 'checks'; break;
        case 'call': verb = `calls ${formatAmount(p.toLevel)}`; break;
        case 'bet': verb = `bets ${formatAmount(p.toLevel)}`; break;
        case 'raise': verb = `raises to ${formatAmount(p.toLevel)}`; break;
        default: return null;
      }
      let t = `${who} ${verb}`;
      if (p.allIn) t += ' (all in)';
      return makeLine(event, 'action', [textSeg(t)]);
    }

    case 'STREET_ADVANCED': {
      const street = String(p.street || '').toLowerCase();
      const label = street ? street.charAt(0).toUpperCase() + street.slice(1) : 'Street';
      const board = String(p.board || '').trim().split(/\s+/).filter(Boolean);
      let shown;
      if (street === 'flop') shown = board.slice(0, 3);
      else if (street === 'turn') shown = board.slice(3, 4);
      else if (street === 'river') shown = board.slice(4, 5);
      else shown = board;
      if (!shown.length) shown = board; // defensive: backend sent only the delta
      return makeLine(event, 'structural', withCards(`${label}: `, shown), 'divider');
    }

    case 'PUP_PLAY': {
      const who = name(p.seat != null ? p.seat : event.actorSeat);
      return makeLine(event, 'pup', [textSeg(`${who} plays `), pupSeg(p.code, pupName(p.code))]);
    }

    case 'PUP_EFFECT_RESOLVED': {
      // The "on {target}" line only exists for a targeted effect; the PUP_PLAY line already told the
      // story otherwise. (PUP_PLAY has no playId, so the two events are formatted independently — a
      // live-only pairing would not reproduce in the replay viewer.)
      if (p.applied === false || p.targetSeat == null) return null;
      return makeLine(event, 'pup', [pupSeg(p.code, pupName(p.code)), textSeg(` on ${name(p.targetSeat)}`)]);
    }

    case 'PUP_PRIVATE_REVEAL': {
      // Server-filtered to this viewer only. Never fabricated for other seats.
      if (p.kind === 'deck') {
        return makeLine(event, 'pup', withCards('You peek the deck: ', p.cards));
      }
      const subject = p.seat != null ? `${name(p.seat)}'s ` : '';
      return makeLine(event, 'pup', withCards(`You see ${subject}`, p.cards));
    }

    case 'CARD_MODIFIED': {
      const before = cardSegment(p.before);
      const after = cardSegment(p.after);
      if (!before || !after) return null;
      const via = [textSeg(' (via '), pupSeg(p.code, pupName(p.code)), textSeg(')')];
      if (p.zone === 'HOLE') {
        // A hole-card change is private to its owner (delivered only to that seat).
        if (viewer == null || p.ownerSeat !== viewer) return null;
        return makeLine(event, 'pup', [textSeg('Your '), before, textSeg(' became '), after, ...via]);
      }
      return makeLine(event, 'pup', [textSeg('Board card '), before, textSeg(' became '), after, ...via]);
    }

    case 'SHOWDOWN_REVEAL': {
      const segs = withCards(`${name(p.seat)} shows `, p.cards);
      if (p.handName) segs.push(textSeg(` (${p.handName})`));
      return makeLine(event, 'structural', segs);
    }

    case 'SHOWDOWN_MUCK':
      return makeLine(event, 'structural', [textSeg(`${name(p.seat)} mucks`)]);

    case 'POT_AWARD': {
      const who = name(p.seat != null ? p.seat : event.actorSeat);
      let t = `${who} wins ${formatAmount(p.amount)}`;
      if (p.handName) t += ` (${p.handName})`;
      return makeLine(event, 'structural', [textSeg(t)]);
    }

    case 'HAND_END':
      return makeLine(event, 'structural', [textSeg('End of hand')], 'divider');

    default:
      return null;
  }
};

export default formatLogEvent;
